import { useEffect, useMemo, useRef, useState } from "react";
import { arrayRemove, arrayUnion, doc, onSnapshot, serverTimestamp, Timestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import type { EntityType, EventOption, InteractionEvent } from "../types";
import { getEventOptionsForEntity } from "../config/defaultEventOptions";
import { useTypeSettings } from "../contexts/TypeSettingsContext";
import { deviceZone } from "../utils/tripDisplay";

/** How long the "Logged · Undo" bar stays up after a tap. */
export const UNDO_MS = 6000;

/**
 * The entity's own step list. Listened to rather than read once, so a new
 * button order saved from the card shows straight away; the listener serves
 * the cached copy first, so it works offline too.
 */
export function useEntityEventOptions(entityId: string | null | undefined): EventOption[] | undefined {
  const [options, setOptions] = useState<EventOption[] | undefined>(undefined);
  useEffect(() => {
    setOptions(undefined);
    if (!entityId) return;
    return onSnapshot(
      doc(db, "entities", entityId),
      (s) => {
        const data = s.data();
        if (data) setOptions((data.eventOptions as EventOption[] | undefined) ?? undefined);
      },
      () => {},
    );
  }, [entityId]);
  return options;
}

/** The steps an activity offers: its place's own list, else its type's. */
export function useActivityStepOptions(entityId: string | null | undefined, type: EntityType | undefined): EventOption[] {
  const entityOptions = useEntityEventOptions(entityId);
  const typeSettings = useTypeSettings(type);
  return useMemo(
    () => getEventOptionsForEntity(entityOptions, type, typeSettings?.eventOptions),
    [entityOptions, type, typeSettings],
  );
}

/**
 * One-tap step logging for an activity, with Undo instead of a confirm. The
 * returned `events` include taps the snapshot hasn't echoed yet.
 */
export function useStepLogger(activityId: string, stored: InteractionEvent[] | undefined) {
  const [pending, setPending] = useState<InteractionEvent[]>([]);
  const events = useMemo(() => {
    const list = stored ?? [];
    const extra = pending.filter((p) => !list.some((s) => s.eventTimestamp.isEqual(p.eventTimestamp)));
    return [...list, ...extra].sort((a, b) => a.eventTimestamp.toMillis() - b.eventTimestamp.toMillis());
  }, [stored, pending]);

  const [busy, setBusy] = useState(false);
  const [undo, setUndo] = useState<{ event: InteractionEvent; label: string } | null>(null);
  const undoTimer = useRef<number | null>(null);
  useEffect(() => () => {
    if (undoTimer.current) window.clearTimeout(undoTimer.current);
  }, []);

  const ref = doc(db, "interactions", activityId);

  const logStep = async (opt: EventOption) => {
    if (busy) return;
    setBusy(true);
    const event: InteractionEvent = {
      eventType: opt.eventType,
      eventTimestamp: Timestamp.now(),
      notes: null,
      customLabel: opt.displayName,
      timezone: deviceZone() ?? null,
    };
    setPending((p) => [...p, event]);
    setUndo({ event, label: opt.displayName });
    if (undoTimer.current) window.clearTimeout(undoTimer.current);
    undoTimer.current = window.setTimeout(() => setUndo(null), UNDO_MS);
    try {
      // Added on its own, never by writing the array back, so a watch's
      // offline steps aren't overwritten (same as the activity page).
      await updateDoc(ref, { events: arrayUnion(event), updatedAt: serverTimestamp() });
    } catch {
      setPending((p) => p.filter((e) => e !== event));
      setUndo(null);
    } finally {
      setBusy(false);
    }
  };

  const undoStep = async () => {
    if (!undo) return;
    const { event } = undo;
    setUndo(null);
    setPending((p) => p.filter((e) => e !== event));
    try {
      await updateDoc(ref, { events: arrayRemove(event), updatedAt: serverTimestamp() });
    } catch {
      // It stays logged; the timeline shows it and it can be removed there.
    }
  };

  return { events, busy, undo, logStep, undoStep };
}

/**
 * Which steps to put on buttons: the most likely one, plus up to two
 * alternatives when the flow branches. The step that starts the thing itself
 * ("On Ride", "Show Started") always makes the cut, since a queue can end from
 * any point and that's the tap that matters most.
 */
export function pickStepButtons(
  options: EventOption[],
  branching: boolean,
): { primary: EventOption | undefined; secondary: EventOption[] } {
  const [primary, ...others] = options;
  const mainStart = others.find((o) => o.category === "ACTIVE");
  const secondary = branching
    ? mainStart && !others.slice(0, 2).includes(mainStart)
      ? [others[0]!, mainStart]
      : others.slice(0, 2)
    : [];
  return { primary, secondary };
}
